// utils/sessionGrouping.js
import moment from "moment";

/**
 * Groups sessions and returns them as containers for UI rendering
 * Group sessions will be in a single container with multiple students
 */
export const groupSessionsForDisplay = (sessions) => {
  const groupMap = {};
  const result = [];

  // First pass: Group by trainer + time (ignoring minor variations in package names to keep groups together)
  sessions.forEach((session) => {
    // Group by trainer + time + session TYPE only (not package).
    // "Reformer 4'lü 8 Ders" and "Reformer 4'lü 12 Ders" share the same sessionTypeId
    // and should be grouped together — the package is just the payment plan.
    const typeKey = session.sessionTypeId || "unknown";
    const groupKey = `${session.trainerId}_${moment(session.start).format("YYYY-MM-DD HH:mm")}_${typeKey}`;

    if (!groupMap[groupKey]) {
      groupMap[groupKey] = [];
    }
    groupMap[groupKey].push(session);
  });

  // Second pass: Create display items
  Object.entries(groupMap).forEach(([groupKey, groupSessions]) => {
    if (groupSessions.length > 1) {
      // This is a group - create a container
      const studentNames = groupSessions.map((s) => s.studentName).join(", ");
      
      // Determine package name summary
      const uniquePackages = [...new Set(groupSessions.map(s => s.packageName || s.sessionType))].filter(Boolean);
      const displayPackageName = uniquePackages.length > 1 ? "Karma Paket" : (uniquePackages[0] || "Grup Dersi");

      result.push({
        isGroupContainer: true,
        groupId: groupKey,
        groupSize: groupSessions.length,
        trainerId: groupSessions[0].trainerId,
        trainerName: groupSessions[0].trainerName,
        packageName: displayPackageName,
        start: groupSessions[0].start,
        end: groupSessions[0].end,
        status: groupSessions[0].status,
        students: groupSessions.map((s) => ({
          id: s.id,
          studentId: s.studentId,
          studentName: s.studentName,
          notes: s.notes,
          session: s,
        })),
        organizationId: groupSessions[0].organizationId,
        id: `group_${groupKey}`,
        color: groupSessions[0].color,
        title: `${studentNames} - ${displayPackageName}`,
        studentName: studentNames,
      });
    } else {
      // Solo session - add as is
      result.push({
        ...groupSessions[0],
        isGroupContainer: false,
      });
    }
  });

  return result;
};

/**
 * Get group color based on hash
 */
export const getGroupColor = (groupId) => {
  const colors = [
    {
      border: "border-purple-500/40",
      bg: "bg-gradient-to-br from-purple-500/20 to-purple-500/5 backdrop-blur-sm",
      text: "text-purple-300",
    },
    {
      border: "border-blue-500/40",
      bg: "bg-gradient-to-br from-blue-500/20 to-blue-500/5 backdrop-blur-sm",
      text: "text-blue-300",
    },
    {
      border: "border-emerald-500/40",
      bg: "bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 backdrop-blur-sm",
      text: "text-emerald-300",
    },
    {
      border: "border-orange-500/40",
      bg: "bg-gradient-to-br from-orange-500/20 to-orange-500/5 backdrop-blur-sm",
      text: "text-orange-300",
    },
    {
      border: "border-pink-500/40",
      bg: "bg-gradient-to-br from-pink-500/20 to-pink-500/5 backdrop-blur-sm",
      text: "text-pink-300",
    },
    {
      border: "border-cyan-500/40",
      bg: "bg-gradient-to-br from-cyan-500/20 to-cyan-500/5 backdrop-blur-sm",
      text: "text-cyan-300",
    },
  ];

  let hash = 0;
  for (let i = 0; i < groupId.length; i++) {
    hash = (hash << 5) - hash + groupId.charCodeAt(i);
    hash |= 0;
  }

  return colors[Math.abs(hash) % colors.length];
};
